/**
 * Checkout Queue Manager
 *
 * จัดการคิวการคิดเงินเพื่อป้องกัน rate limit errors
 * เมื่อมีการคิดเงินหลายโต๊ะพร้อมกัน
 *
 * Features:
 * - จำกัดจำนวน checkout ที่ทำงานพร้อมกัน
 * - จัดการ error และ retry อัตโนมัติ
 * - แจ้งสถานะการประมวลผล
 */

const CHECKOUT_TIMEOUT_MS = 30000; // 30 วินาที

function withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`Checkout timeout (${ms}ms): ${label}`)), ms)
    )
  ]);
}

class CheckoutQueue {
  constructor(concurrency = 2) {
    this.queue = []; // คิวรอประมวลผล
    this.active = 0; // จำนวน checkout ที่กำลังทำงาน
    this.concurrency = concurrency; // จำนวนสูงสุดที่ทำงานพร้อมกัน
    this.listeners = []; // callback สำหรับแจ้งสถานะ
  }

  /**
   * เพิ่ม checkout เข้าคิว
   * @param {Function} checkoutFn - ฟังก์ชัน async ที่ทำการ checkout
   * @param {Object} metadata - ข้อมูลเพิ่มเติม (tableCode, tableName, etc.)
   * @returns {Promise} - resolve เมื่อ checkout สำเร็จ
   */
  async add(checkoutFn, metadata = {}) {
    return new Promise((resolve, reject) => {
      const item = {
        checkoutFn,
        metadata,
        resolve,
        reject,
        addedAt: Date.now()
      };

      this.queue.push(item);
      this.notifyStatusChange();
      this.process();
    });
  }

  /**
   * ประมวลผลคิว
   */
  async process() {
    // ถ้าทำงานเต็มแล้ว หรือคิวว่าง ไม่ต้องทำอะไร
    while (this.active < this.concurrency && this.queue.length > 0) {
      const item = this.queue.shift();
      this.active++;
      this.notifyStatusChange();

      try {
        console.log(`[CheckoutQueue] กำลังประมวลผล: ${item.metadata.tableCode || 'unknown'} (active: ${this.active}, pending: ${this.queue.length})`);

        const result = await withTimeout(
          item.checkoutFn(),
          CHECKOUT_TIMEOUT_MS,
          item.metadata.tableCode || 'unknown'
        );
        item.resolve(result);

        console.log(`[CheckoutQueue] สำเร็จ: ${item.metadata.tableCode || 'unknown'}`);
      } catch (error) {
        console.error(`[CheckoutQueue] ล้มเหลว: ${item.metadata.tableCode || 'unknown'}`, error);
        item.reject(error);
      } finally {
        this.active--;
        this.notifyStatusChange();

        // ประมวลผลรายการถัดไปในคิว
        this.process();
      }
    }
  }

  /**
   * ลงทะเบียน callback สำหรับแจ้งเปลี่ยนสถานะคิว
   * @param {Function} callback - ฟังก์ชันที่จะถูกเรียกเมื่อสถานะเปลี่ยน
   */
  onStatusChange(callback) {
    this.listeners.push(callback);
  }

  /**
   * ยกเลิกการลงทะเบียน callback
   * @param {Function} callback - ฟังก์ชันที่ต้องการยกเลิก
   */
  offStatusChange(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  /**
   * แจ้งเปลี่ยนสถานะไปยัง listeners ทั้งหมด
   */
  notifyStatusChange() {
    const status = this.getStatus();
    this.listeners.forEach(listener => {
      try {
        listener(status);
      } catch (error) {
        console.error('[CheckoutQueue] Error in status listener:', error);
      }
    });
  }

  /**
   * ดึงสถานะปัจจุบันของคิว
   * @returns {Object} - ข้อมูลสถานะ
   */
  getStatus() {
    return {
      active: this.active, // จำนวนที่กำลังทำงาน
      pending: this.queue.length, // จำนวนที่รออยู่
      total: this.active + this.queue.length, // รวมทั้งหมด
      concurrency: this.concurrency, // จำนวนสูงสุดที่ทำงานพร้อมกัน
      isEmpty: this.active === 0 && this.queue.length === 0
    };
  }

  /**
   * ล้างคิวทั้งหมด (ใช้เฉพาะกรณีฉุกเฉิน)
   */
  clear() {
    const cleared = this.queue.length;
    this.queue.forEach(item => {
      item.reject(new Error('คิวถูกยกเลิก'));
    });
    this.queue = [];
    this.notifyStatusChange();
    console.log(`[CheckoutQueue] ยกเลิกคิว ${cleared} รายการ`);
  }

  /**
   * เปลี่ยนจำนวน concurrency ระหว่างทำงาน
   * @param {number} newConcurrency - จำนวนใหม่
   */
  setConcurrency(newConcurrency) {
    if (newConcurrency < 1) {
      throw new Error('Concurrency must be at least 1');
    }
    this.concurrency = newConcurrency;
    console.log(`[CheckoutQueue] เปลี่ยน concurrency เป็น ${newConcurrency}`);
    this.notifyStatusChange();
    this.process(); // เริ่มประมวลผลเพิ่มถ้า concurrency เพิ่ม
  }
}

// Export singleton instance
// ใช้ concurrency = 2 (คิดเงิน 2 โต๊ะพร้อมกันสูงสุด)
export const checkoutQueue = new CheckoutQueue(2);

// Export class สำหรับ testing
export { CheckoutQueue };
